<div class="page-container">
  <app-card>
    <app-card-header>
      <app-card-title>
        <div class="header-row flex flex-align-center gap-lg">
          @if (checkPoint()) {
            <app-checkpoint-form-header
              class="mr-auto"
              [checkpointName]="checkPoint()!.name"
              [formName]="$safeNavigationMigration(form()?.name)"
            />
          } @else {
            <span class="mr-auto">{{ 'labelPrinting.title' | translate }}</span>
          }
          <div class="print-actions flex gap-md">
            <app-button variant="outlined" (click)="goBack()" data-testid="cancel-btn">
              {{ 'labelPrinting.action.back' | translate }}
            </app-button>
            @if (false) {
              <app-button
                variant="outlined"
                (click)="debug.set(!debug())"
                data-testid="debug-btn"
                [class.debug-active]="debug()"
              >
                🐛 Debug{{ debug() ? ' ON' : '' }}
              </app-button>
            }
            <app-button
              variant="filled"
              [disabled]="!canPrint || generating()"
              (click)="onPrint()"
              data-testid="print-btn"
            >
              @if (generating()) {
                <app-spinner />
              } @else {
                <app-icon name="printer" />
              }
              {{ 'labelPrinting.action.print' | translate }}
              @if (selectedCount > 0) {
                ({{ selectedCount }})
              }
            </app-button>
          </div>
        </div>
      </app-card-title>
    </app-card-header>

    <app-card-content>
      <!-- Sheet format action buttons — injected as template-content into the sheet section -->
      <ng-template #sheetActionsTemplate>
        <div class="format-actions" data-testid="sheet-format-actions">
          <app-button
            variant="outlined"
            size="sm"
            (click)="onCreateSheetFormat()"
            data-testid="new-sheet-format-btn"
          >
            <app-icon name="plus" />
            {{ 'labelPrinting.sheetFormatActions.new' | translate }}
          </app-button>
          <app-button
            variant="icon"
            size="sm"
            [disabled]="!canEditSelectedSheetFormat"
            (click)="onEditSelectedSheetFormat()"
            data-testid="edit-sheet-format-btn"
          >
            <app-icon name="pencil" />
          </app-button>
          <app-button
            variant="icon"
            color="danger"
            size="sm"
            [disabled]="!canDeleteSelectedSheetFormat"
            (click)="onDeleteSelectedSheetFormat()"
            data-testid="delete-sheet-format-btn"
          >
            <app-icon name="trash-2" />
          </app-button>
        </div>
      </ng-template>

      <!-- Label preview template — injected as template-content into the preview section -->
      <ng-template #labelPreviewTemplate>
        @if (selectedLayout && selectedSheetFormat) {
          <div
            class="preview-container"
            [style.aspect-ratio]="
              selectedSheetFormat.label_width / selectedSheetFormat.label_height
            "
          >
            <app-label-preview
              [layout]="selectedLayout"
              [checkpointName]="checkPoint()!.name"
              [formName]="form()!.name"
              [showCode]="printConfigModel.showCode"
              [showCheckpointName]="printConfigModel.showCheckpointName"
              [showFormName]="printConfigModel.showFormName"
              [showSerialNumber]="printConfigModel.showSerialNumber"
              [serialNumber]="instances().length > 0 ? '#' + instances()[0].serial_number : ''"
              [checkpointNameFontSize]="printConfigModel.checkpointNameFontSize"
              [formNameFontSize]="printConfigModel.formNameFontSize"
              [serialNumberFontSize]="printConfigModel.serialNumberFontSize"
              [fontFamily]="printConfigModel.fontFamily"
              [codeType]="printConfigModel.codeType"
              [qrX]="printConfigModel.qrX"
              [qrY]="printConfigModel.qrY"
              [qrWidth]="printConfigModel.qrWidth"
              [qrHeight]="printConfigModel.qrHeight"
              [checkpointNameX]="printConfigModel.checkpointNameX"
              [checkpointNameY]="printConfigModel.checkpointNameY"
              [checkpointNameWidth]="printConfigModel.checkpointNameWidth"
              [checkpointNameHeight]="printConfigModel.checkpointNameHeight"
              [formNameX]="printConfigModel.formNameX"
              [formNameY]="printConfigModel.formNameY"
              [formNameWidth]="printConfigModel.formNameWidth"
              [formNameHeight]="printConfigModel.formNameHeight"
              [serialNumberX]="printConfigModel.serialNumberX"
              [serialNumberY]="printConfigModel.serialNumberY"
              [serialNumberWidth]="printConfigModel.serialNumberWidth"
              [serialNumberHeight]="printConfigModel.serialNumberHeight"
              [labelWidthMm]="selectedSheetFormat.label_width"
              [labelHeightMm]="selectedSheetFormat.label_height"
              [editable]="true"
              (boundsChange)="onPreviewBoundsChange($event)"
            />
          </div>
        }
      </ng-template>

      @if (loading()) {
        <div
          class="loading-container flex flex-column flex-align-center flex-justify-center gap-md"
        >
          <app-spinner></app-spinner>
          <p>{{ 'labelPrinting.loading' | translate }}</p>
        </div>
      } @else if (error()) {
        <div class="error-message">
          <p>{{ error() }}</p>
          <app-button variant="outlined" (click)="goBack()" data-testid="back-btn">
            <app-icon name="arrow-left" />
            {{ 'labelPrinting.action.back' | translate }}
          </app-button>
        </div>
      } @else {
        <div class="label-printing-content">
          <!-- Config form: all sections in a single Formly form with two-column layout -->
          <div class="config-form-container" data-testid="config-form">
            <formly-form
              [form]="mainForm"
              [fields]="mainFields"
              [model]="printConfigModel"
              (modelChange)="onMainModelChange($event)"
            />
          </div>

          <!-- Label Position Selection -->
          @if (selectedSheetFormat && selectedLayout) {
            <section class="step-section" data-testid="grid-section">
              <div class="step-header flex flex-align-center flex-justify-space-between">
                <h3 class="step-title">
                  {{ 'labelPrinting.step.selectPositions' | translate }}
                  <span class="selection-count">({{ selectedCount }} / {{ totalLabels }})</span>
                </h3>
                <div class="grid-actions flex gap-sm">
                  @if (!hasInstances()) {
                    <app-button
                      variant="outlined"
                      size="sm"
                      (click)="onSelectAll()"
                      data-testid="select-all-btn"
                    >
                      {{ 'labelPrinting.action.selectAll' | translate }}
                    </app-button>
                    <app-button
                      variant="outlined"
                      size="sm"
                      (click)="onMarkLabeledAsUsed()"
                      [disabled]="selectedCount === 0"
                      data-testid="mark-labeled-as-used-btn"
                    >
                      {{ 'labelPrinting.action.markLabeledAsUsed' | translate }}
                    </app-button>
                    <app-button
                      variant="outlined"
                      size="sm"
                      (click)="onClearAll()"
                      data-testid="clear-all-btn"
                    >
                      {{ 'labelPrinting.action.clearAll' | translate }}
                    </app-button>
                  } @else {
                    <app-button
                      variant="outlined"
                      size="sm"
                      (click)="autoFillInstances()"
                      [disabled]="assignableInstances.length === 0"
                      data-testid="auto-fill-btn"
                    >
                      <app-icon name="zap" />
                      {{ 'labelPrinting.instances.autoFill' | translate }}
                    </app-button>
                    <app-button
                      variant="outlined"
                      size="sm"
                      (click)="clearInstanceAssignments()"
                      [disabled]="assignedCount === 0"
                      data-testid="clear-assignments-btn"
                    >
                      {{ 'labelPrinting.instances.clearAll' | translate }}
                    </app-button>
                    <app-button
                      variant="outlined"
                      size="sm"
                      (click)="onClearAll()"
                      data-testid="reset-sheet-state-btn"
                    >
                      {{ 'labelPrinting.action.clearAll' | translate }}
                    </app-button>
                  }
                </div>
              </div>

              @if (hasInstances()) {
                <p class="step-description">{{ 'labelPrinting.instances.hint' | translate }}</p>

                <div class="instance-grid-layout">
                  <!-- Instance sidebar -->
                  <aside class="instance-sidebar" data-testid="instance-sidebar">
                    <h4 class="sidebar-title">
                      {{ 'labelPrinting.instances.title' | translate }}
                      <span class="sidebar-count"
                        >({{ assignedCount }} / {{ instances().length }})</span
                      >
                    </h4>
                    <div class="instance-list">
                      @for (instance of assignableInstances; track instance.id) {
                        <button
                          class="instance-chip"
                          [class.selected]="selectedInstance()?.id === instance.id"
                          (click)="selectInstance(instance)"
                          [attr.data-testid]="'instance-chip-' + instance.serial_number"
                        >
                          {{ instance.serial_number }}
                        </button>
                      }
                      @if (assignableInstances.length === 0) {
                        <p class="all-assigned">
                          {{ 'labelPrinting.instances.allAssigned' | translate }}
                        </p>
                      }
                    </div>
                  </aside>

                  <!-- Sheet grid -->
                  <div class="instance-grid-main">
                    <app-sheet-grid
                      [sheetFormat]="selectedSheetFormat!"
                      [labeledPositions]="labeledPositions()"
                      [usedPositions]="usedPositions()"
                      [labelLayout]="selectedLayout"
                      [checkpointName]="checkPoint()!.name"
                      [formName]="form()!.name"
                      [showCheckpointName]="printConfigModel.showCheckpointName"
                      [showFormName]="printConfigModel.showFormName"
                      [qrUrl]="qrUrl"
                      [checkpointNameFontSize]="printConfigModel.checkpointNameFontSize"
                      [formNameFontSize]="printConfigModel.formNameFontSize"
                      [fontFamily]="printConfigModel.fontFamily"
                      [codeType]="printConfigModel.codeType"
                      [qrX]="printConfigModel.qrX"
                      [qrY]="printConfigModel.qrY"
                      [qrWidth]="printConfigModel.qrWidth"
                      [qrHeight]="printConfigModel.qrHeight"
                      [showCode]="printConfigModel.showCode"
                      [checkpointNameX]="printConfigModel.checkpointNameX"
                      [checkpointNameY]="printConfigModel.checkpointNameY"
                      [checkpointNameWidth]="printConfigModel.checkpointNameWidth"
                      [formNameX]="printConfigModel.formNameX"
                      [formNameY]="printConfigModel.formNameY"
                      [formNameWidth]="printConfigModel.formNameWidth"
                      [checkpointNameHeight]="printConfigModel.checkpointNameHeight"
                      [formNameHeight]="printConfigModel.formNameHeight"
                      [showSerialNumber]="printConfigModel.showSerialNumber"
                      [serialNumberFontSize]="printConfigModel.serialNumberFontSize"
                      [serialNumberX]="printConfigModel.serialNumberX"
                      [serialNumberY]="printConfigModel.serialNumberY"
                      [serialNumberWidth]="printConfigModel.serialNumberWidth"
                      [serialNumberHeight]="printConfigModel.serialNumberHeight"
                      [positionSerials]="positionSerials"
                      [debug]="debug()"
                      (positionToggled)="onPositionToggled($event)"
                      (positionRightClicked)="onPositionRightClicked($event)"
                    />
                  </div>
                </div>
              } @else {
                <p class="step-description">
                  {{ 'labelPrinting.step.selectPositionsHint' | translate }}
                </p>
                <app-sheet-grid
                  [sheetFormat]="selectedSheetFormat!"
                  [labeledPositions]="labeledPositions()"
                  [usedPositions]="usedPositions()"
                  [labelLayout]="selectedLayout"
                  [checkpointName]="checkPoint()!.name"
                  [formName]="form()!.name"
                  [showCheckpointName]="printConfigModel.showCheckpointName"
                  [showFormName]="printConfigModel.showFormName"
                  [qrUrl]="qrUrl"
                  [checkpointNameFontSize]="printConfigModel.checkpointNameFontSize"
                  [formNameFontSize]="printConfigModel.formNameFontSize"
                  [fontFamily]="printConfigModel.fontFamily"
                  [codeType]="printConfigModel.codeType"
                  [qrX]="printConfigModel.qrX"
                  [qrY]="printConfigModel.qrY"
                  [qrWidth]="printConfigModel.qrWidth"
                  [qrHeight]="printConfigModel.qrHeight"
                  [showCode]="printConfigModel.showCode"
                  [checkpointNameX]="printConfigModel.checkpointNameX"
                  [checkpointNameY]="printConfigModel.checkpointNameY"
                  [checkpointNameWidth]="printConfigModel.checkpointNameWidth"
                  [formNameX]="printConfigModel.formNameX"
                  [formNameY]="printConfigModel.formNameY"
                  [formNameWidth]="printConfigModel.formNameWidth"
                  [checkpointNameHeight]="printConfigModel.checkpointNameHeight"
                  [formNameHeight]="printConfigModel.formNameHeight"
                  [showSerialNumber]="printConfigModel.showSerialNumber"
                  [serialNumberFontSize]="printConfigModel.serialNumberFontSize"
                  [serialNumberX]="printConfigModel.serialNumberX"
                  [serialNumberY]="printConfigModel.serialNumberY"
                  [serialNumberWidth]="printConfigModel.serialNumberWidth"
                  [serialNumberHeight]="printConfigModel.serialNumberHeight"
                  [positionSerials]="positionSerials"
                  [debug]="debug()"
                  (positionToggled)="onPositionToggled($event)"
                  (positionRightClicked)="onPositionRightClicked($event)"
                />
              }
            </section>
          }
        </div>
      }
    </app-card-content>
  </app-card>
</div>
